import { useState } from 'react';
import { X } from 'lucide-react';
import { TIER_META, STATUS_OPTIONS } from './vipTiers';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Founding-100 registration modal.
 *
 * Owns its own form state — when the user clicks "Reserve my spot",
 * POSTs to /api/vip/register, displays success or error, and on success
 * calls `onRegistered(tier)` so the parent can refresh live counts.
 */
export default function VIPRegistrationModal({ tierKey, onClose, onRegistered }) {
  const meta = TIER_META[tierKey];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    current_status: '',
    weekly_volume: '',
    current_blocker: '',
    hope_for_tool: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.current_status) {
      setResult({ error: 'Please tell us your current reselling status.' });
      return;
    }
    setIsSubmitting(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/vip/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, tier: tierKey }),
      });
      const data = await res.json();
      if (res.ok) {
        setResult({ success: true, ...data });
        onRegistered?.(tierKey);
      } else {
        setResult({ error: data.detail || 'Something went wrong. Please try again.' });
      }
    } catch {
      setResult({ error: 'Network error. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const update = (key) => (e) => setFormData({ ...formData, [key]: e.target.value });

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="vip-modal"
    >
      <div
        className="bg-[#0A0A0A] border border-[#FFD700]/30 rounded-2xl max-w-lg w-full my-8 shadow-[0_0_60px_rgba(255,215,0,0.2)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <div>
            <div className="text-xs font-bold text-[#FFD700] tracking-wider">FOUNDING 100</div>
            <h3 className="text-xl font-bold text-white">Reserve your {meta.label} spot</h3>
          </div>
          <button onClick={onClose} data-testid="vip-modal-close" className="text-zinc-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        {result?.success ? (
          <SuccessPanel tierKey={tierKey} result={result} onClose={onClose} />
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4" data-testid="vip-form">
            <TextField label="Your name" required value={formData.name} onChange={update('name')} testid="vip-input-name" />
            <TextField type="email" label="Email address" required value={formData.email} onChange={update('email')} testid="vip-input-email" />

            <div>
              <Label required>Your current reselling status</Label>
              <select
                required
                value={formData.current_status}
                onChange={update('current_status')}
                data-testid="vip-input-status"
                className={INPUT_CLASS}
              >
                <option value="">Select one…</option>
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="pt-2 border-t border-zinc-800">
              <p className="text-xs text-zinc-500 mb-3">
                The rest is <span className="text-red-400 font-bold">(optional)</span> — skip
                if you&rsquo;re in a hurry. Your answers help us build what you actually need.
              </p>
            </div>

            <div>
              <Label optional>Items you sell/flip per week</Label>
              <select value={formData.weekly_volume} onChange={update('weekly_volume')} data-testid="vip-input-volume" className={INPUT_CLASS}>
                <option value="">Prefer not to say</option>
                <option value="0-10">0–10 per week</option>
                <option value="10-50">10–50 per week</option>
                <option value="50-200">50–200 per week</option>
                <option value="200+">200+ per week</option>
              </select>
            </div>

            <TextArea
              label="What’s stopping you selling more right now?"
              optional
              value={formData.current_blocker}
              onChange={update('current_blocker')}
              placeholder="e.g. too time-consuming to check prices, missing arbitrage opportunities…"
              testid="vip-input-blocker"
            />
            <TextArea
              label="What do you hope this tool will do for you?"
              optional
              value={formData.hope_for_tool}
              onChange={update('hope_for_tool')}
              placeholder="e.g. save hours, never miss an arbitrage, run my shop smarter…"
              testid="vip-input-hope"
            />

            {result?.error && (
              <div className="bg-red-900/30 border border-red-500/40 rounded-lg p-3 text-sm text-red-300" data-testid="vip-error">
                {result.error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="vip-submit"
              className="w-full bg-gradient-to-r from-[#00D26A] to-[#FFD700] text-black font-bold px-6 py-4 rounded-full text-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              {isSubmitting ? 'Reserving your spot…' : `Reserve my Founding 100 spot — £${meta.vip}`}
            </button>
            <p className="text-xs text-zinc-500 text-center">
              Interest only — no payment taken now. You&rsquo;ll claim at launch.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

// ---------- Small form primitives ----------
const INPUT_CLASS =
  'w-full px-4 py-3 bg-[#050505] border border-zinc-700 rounded-lg text-white focus:border-[#00D26A] focus:outline-none';

const Label = ({ required, optional, children }) => (
  <label className="block text-sm font-medium text-zinc-300 mb-1">
    {children}{' '}
    {required && <span className="text-red-400">*</span>}
    {optional && <span className="text-red-400">(optional)</span>}
  </label>
);

const TextField = ({ label, required, type = 'text', value, onChange, testid }) => (
  <div>
    <Label required={required}>{label}</Label>
    <input type={type} required={required} value={value} onChange={onChange} data-testid={testid} className={INPUT_CLASS} />
  </div>
);

const TextArea = ({ label, optional, value, onChange, placeholder, testid }) => (
  <div>
    <Label optional={optional}>{label}</Label>
    <textarea rows="2" value={value} onChange={onChange} data-testid={testid} placeholder={placeholder} className={INPUT_CLASS} />
  </div>
);

function SuccessPanel({ tierKey, result, onClose }) {
  const meta = TIER_META[tierKey];
  return (
    <div className="p-8 text-center" data-testid="vip-success">
      <div className="text-5xl mb-4">🎉</div>
      <h4 className="text-2xl font-bold text-[#FFD700] mb-2">You&rsquo;re in!</h4>
      <p className="text-zinc-300 mb-2">
        Position <span className="font-bold text-[#00D26A]">#{result.position}</span> in {meta.label} tier
      </p>
      {result.spots_left !== undefined && (
        <p className="text-sm text-zinc-500 mb-6">{result.spots_left} spots remaining in this tier</p>
      )}
      {result.hobbyist_flag && (
        <div className="bg-[#3a1f0a] border border-[#FFA500]/50 rounded-lg p-4 mb-6 text-left">
          <p className="text-sm text-[#FFA500]">
            <strong>Heads up:</strong> the Founding 100 offer is designed for regular resellers.
            Since you selected &ldquo;Home Collection&rdquo;, you may find our Home Collection
            tier a better fit — £5/month, 30-day free trial, launching September 2026. We&rsquo;ll
            reach out to help you pick the right option.
          </p>
        </div>
      )}
      <p className="text-sm text-zinc-400 mb-6">
        Check your inbox — a confirmation email from{' '}
        <span className="text-[#00D26A]">hello@flippingfriends.co.uk</span> is on its way.
      </p>
      <button
        onClick={onClose}
        className="bg-gradient-to-r from-[#00D26A] to-[#FFD700] text-black font-bold px-8 py-3 rounded-full hover:opacity-90 transition-opacity"
      >
        Done
      </button>
    </div>
  );
}
